/**
 * Stable reconciliation GL-posting failure contract shared by service producers, API transport,
 * and the recovery UI.  Add a code here before emitting it; consumers must not infer semantics
 * from error-message text or a string prefix.
 */

import { AR_RECEIPT_REFUSAL_CODES, AR_RECEIPT_REFUSAL_MESSAGES } from './ar-receipt-refusals.js';
import { RECON_V2_ADJUSTMENT_ERROR_CODES } from './decision-queue-v2.js';
import {
  PAYMENT_MISMATCH_REFUSAL_CODES,
  PAYMENT_MISMATCH_REFUSAL_MESSAGES,
} from './payment-mismatch.js';

export const RECONCILIATION_GL_POSTING_ERROR_CODES = [
  'SYSTEM_ACCOUNT_NOT_CONFIGURED',
  'NO_OPEN_PERIOD',
  'OP_BANK_ACCOUNT_MISSING',
  'OP_BANK_ACCOUNT_INVALID',
  'OP_INVESTMENT_SHORT_TERM_MISSING',
  'OP_INVESTMENT_SHORT_TERM_INVALID',
  'OP_INVESTMENT_LONG_TERM_MISSING',
  'BANK_ACCOUNT_GL_OFFSET_INVALID',
  'CASH_ACCOUNT_GL_LINK_REQUIRED',
  'CASH_ACCOUNT_GL_LINK_INVALID',
  'CASH_ACCOUNT_GL_LINK_CONFLICT',
  'BANK_ACCOUNT_CURRENCY_IMMUTABLE',
  'AP_VENDOR_LIABILITY_MISSING',
  'AP_VENDOR_LIABILITY_INVALID',
  'AP_PAYMENT_CURRENCY_BASIS_REQUIRED',
  'ADJUSTED_PAYMENT_CASH_BASIS_MISMATCH',
  'PAYMENT_TRIGGERED_RETENTION_ROUTE_UNSUPPORTED',
  'PAYMENT_TRIGGERED_RETENTION_INSTALLMENT_BASIS_INVALID',
  'PAYMENT_TRIGGERED_RETENTION_BASIS_IMMUTABLE',
  'PAYMENT_TRIGGERED_RETENTION_INSTALLMENT_ALLOCATION_INVALID',
  'PAYMENT_TRIGGERED_RETENTION_JOURNAL_MISSING',
  'PAYMENT_TRIGGERED_RETENTION_LINE_MISSING',
  'STATUTORY_DOCUMENT_MISSING',
  'STATUTORY_POSTING_USER_REQUIRED',
  'STATUTORY_EXPLICIT_LINE_ALLOCATION_REQUIRED',
  'STATUTORY_EXPLICIT_ALLOCATION_INVALID_TARGET',
  'STATUTORY_EXPLICIT_ALLOCATION_INVALID',
  'STATUTORY_EXPLICIT_ALLOCATION_TOTAL_MISMATCH',
  'STATUTORY_EXPLICIT_ALLOCATION_EXCEEDS_REMAINING',
  'STATUTORY_PAYMENT_BOOK_BALANCE_MISMATCH',
  'STATUTORY_PAYMENT_EVIDENCE_FORBIDDEN',
  'STATUTORY_SLOT_RESOLUTION_FAILED',
  'STATUTORY_SCHEDULED_PAYMENT_DATE_REQUIRED',
  'STATUTORY_SOURCE_LIABILITY_ORIGIN_REQUIRED',
  'STATUTORY_TAX_LINE_SETTLEMENT_OVERRIDE_REQUIRES_STAGED_APPROVAL',
  'STATUTORY_TAX_LINE_SETTLEMENT_ACCOUNT_INVALID',
  'STATUTORY_TAX_LINE_SETTLEMENT_OVERRIDE_VERSION_MISMATCH',
  'STATUTORY_STAGED_FUNCTIONAL_CURRENCY_REQUIRED',
  'STATUTORY_STAGED_PARTIAL_FREEZE',
  'STATUTORY_STAGED_ACCOUNTING_DATE_MISMATCH',
  'STATUTORY_STAGED_APPROVED_BALANCE_MISSING',
  'STATUTORY_STAGED_APPROVED_BALANCE_INVALID',
  'STATUTORY_STAGED_PAYMENT_ID_REQUIRED',
  'STATUTORY_STAGED_PAYMENT_AMOUNT_MISMATCH',
  'STATUTORY_STAGED_PRINCIPAL_INVALID',
  'STATUTORY_STAGED_PRINCIPAL_ALLOCATION_MISMATCH',
  'STATUTORY_STAGED_SETTLEMENT_SNAPSHOT_MISSING',
  'STATUTORY_STAGED_SETTLEMENT_SNAPSHOT_MISMATCH',
  'STATUTORY_STAGED_SETTLEMENT_RAIL_MISSING',
  'STATUTORY_STAGED_SETTLEMENT_ACCOUNT_INVALID',
  'STATUTORY_STAGED_SETTLEMENT_CROSSWALK_MISSING',
  'STATUTORY_STAGED_SETTLEMENT_OVERRIDE_CROSSWALK_MISSING',
  // ALC-1549 guide fine/interest recognition and settlement. The file contract is "add a code
  // here before emitting it"; these were emitted without being registered, so they are added
  // together rather than only the two this round introduces.
  'STATUTORY_SURCHARGE_ACCOUNT_MAPPING_MISSING',
  'STATUTORY_SURCHARGE_AMOUNT_INVALID',
  'STATUTORY_SURCHARGE_AMOUNT_NEGATIVE',
  'STATUTORY_SURCHARGE_COMPONENT_DUPLICATED',
  'STATUTORY_SURCHARGE_DECREASE_REQUIRES_REVERSAL',
  'STATUTORY_SURCHARGE_LINE_RAIL_MISSING',
  'STATUTORY_SURCHARGE_RECOGNITION_AMOUNT_INVALID',
  'STATUTORY_SURCHARGE_SETTLEMENT_MISMATCH',
  'STATUTORY_SURCHARGE_SETTLEMENT_SNAPSHOT_MISSING',
  'STATUTORY_SURCHARGE_SETTLEMENT_CROSSWALK_MISSING',
  'STATUTORY_SURCHARGE_SETTLEMENT_CROSSWALK_AMBIGUOUS',
  'STATUTORY_SURCHARGE_SETTLEMENT_CROSSWALK_MISMATCH',
  'PAYMENT_STRATEGY_INVALID',
  'AP_SETTLEMENT_ADJUSTMENT_AMOUNT_REQUIRED',
  'AP_SETTLEMENT_ADJUSTMENT_SEMANTIC_REQUIRED',
  'AP_SETTLEMENT_OVERPAYMENT_EXCESS_UNSUPPORTED',
  'AP_SETTLEMENT_ALLOCATION_REQUIRED',
  'AP_SETTLEMENT_AMOUNT_INVALID',
  'AP_SETTLEMENT_BILL_STATUS_STALE',
  'AP_SETTLEMENT_ENTITY_MISMATCH',
  'AP_SETTLEMENT_GROUP_INACTIVE',
  'AP_SETTLEMENT_LOCK_SET_CHANGED_RETRY',
  'AP_SETTLEMENT_PAYMENT_BILL_MISMATCH',
  'AP_SETTLEMENT_PAYMENT_EVIDENCE_REQUIRED',
  'AP_SETTLEMENT_PAYMENT_NOT_TERMINAL',
  'AP_SETTLEMENT_PAYMENT_SOURCE_BIND_FAILED',
  'AP_SETTLEMENT_PAYMENT_SOURCE_MISMATCH',
  'AP_SETTLEMENT_VENDOR_MISMATCH',
  'AP_SETTLEMENT_SOURCE_LINK_NOT_FOUND',
  'AP_SETTLEMENT_SOURCE_LINK_REQUIRED',
  'AP_SETTLEMENT_SOURCE_LINK_AMBIGUOUS',
  'AP_SETTLEMENT_SOURCE_LINK_MISMATCH',
  'AP_SETTLEMENT_SOURCE_TYPE_UNSUPPORTED',
  'AP_SETTLEMENT_SOURCE_ENTITY_MISMATCH',
  'AP_SETTLEMENT_SOURCE_DIRECTION_MISMATCH',
  'AP_SETTLEMENT_SOURCE_SNAPSHOT_MISMATCH',
  'AP_SETTLEMENT_SOURCE_AMOUNT_OR_DATE_REQUIRED',
  'AP_SETTLEMENT_SOURCE_GROUP_TOTAL_MISMATCH',
  'ADVANCE_APPLICATION_AMOUNT_SCALE_INVALID',
  'ADVANCE_APPLICATION_BILL_CURRENCY_NOT_FUNCTIONAL',
  'ADVANCE_APPLICATION_EXCEEDS_BILL_RESIDUAL',
  'ADVANCE_APPLICATION_FX_DIFFERENCE_UNSUPPORTED',
  'ADVANCE_APPLICATION_PAYMENT_CURRENCY_MISMATCH',
  'ADVANCE_APPLICATION_PAYMENT_MISMATCH',
  'ADVANCE_APPLICATION_SOURCE_ACCOUNT_UNSUPPORTED',
  'ADVANCE_APPLICATION_SOURCE_EVIDENCE_INVALID',
  'FOREIGN_CURRENCY_UNSUPPORTED_ADJUSTMENTS',
  // Bill payment refund (estorno bancário) — the scenario refuses these rather than posting a
  // journal it cannot make correct. See `bill-payment-refund.rule.ts` for why each is closed.
  'BILL_PAYMENT_REFUND_FUNCTIONAL_CURRENCY_REQUIRED',
  'BILL_PAYMENT_REFUND_FOREIGN_CURRENCY_UNSUPPORTED',
  'BILL_PAYMENT_REFUND_FX_EVIDENCE_INCOMPLETE',
  'BILL_PAYMENT_REFUND_FX_AMOUNT_MISMATCH',
  'BILL_PAYMENT_REFUND_FX_REALIZED_ACCOUNT_REQUIRED',
  'BILL_PAYMENT_REFUND_TAX_EXPOSURE_REQUIRED',
  'BILL_PAYMENT_REFUND_TAX_EXPOSURE_INVALID',
  'BILL_PAYMENT_REFUND_TAX_REVERSAL_UNSUPPORTED',
  'FOREIGN_SETTLEMENT_ACTIVE_PROVISION_REQUIRED',
  'FOREIGN_SETTLEMENT_ALLOCATION_REQUIRED',
  'FOREIGN_SETTLEMENT_ALLOCATION_UNSUPPORTED',
  'FOREIGN_SETTLEMENT_EVIDENCE_INCOMPLETE',
  'FOREIGN_SETTLEMENT_PROVISION_BOOK_MISMATCH',
  'FOREIGN_SETTLEMENT_SOURCE_TRACE_AMBIGUOUS',
  'FUNCTIONAL_CURRENCY_UNRESOLVED',
  'PROVIDER_SETTLEMENT_SOURCE_ACCOUNT_REQUIRED',
  'SETTLEMENT_PROVISION_BOOK_SET_INCOMPLETE',
  'SETTLEMENT_BOOK_SET_INCOMPLETE',
  'SETTLEMENT_GLOBAL_LINK_DATE_REQUIRED',
  'SETTLEMENT_GLOBAL_LINK_NOT_ELIGIBLE',
  'SETTLEMENT_GLOBAL_LINK_ORDER_VIOLATION',
  'SETTLEMENT_LINK_ORDER_VIOLATION',
  'BILL_NOT_FOUND',
  'PAYMENT_NOT_FOUND',
  // Remaining `ApSettlementRejectedError` reasons. These are surfacing entries only: each one is
  // already thrown by the GL-posting path, and registering it maps that rejection to a 400 with a
  // stable code instead of letting it escape as an unrecognized 500. Registration changes no
  // matching behaviour — `BILL_NOT_APPROVED` in particular still gates GL posting alone, never
  // matching or AUTO_APPLY. The list was previously partial in a way that split siblings from the
  // same throw site (`BILL_NOT_FOUND` registered, `BILL_NOT_APPROVED` and `BANK_TX_NOT_FOUND` not).
  'ADVANCE_APPLICATION_BILL_SCOPE_INVALID',
  'ADVANCE_APPLICATION_EVIDENCE_LINK_FAILED',
  'ADVANCE_APPLICATION_PAYMENT_REFERENCE_REQUIRED',
  'ADVANCE_APPLICATION_POSTING_EVIDENCE_INCOMPLETE',
  'ADVANCE_APPLICATION_REVERSAL_EVIDENCE_INVALID',
  'ADVANCE_APPLICATION_REVERSAL_LINK_FAILED',
  'ADVANCE_BALANCE_SCOPE_REVIEW_REQUIRED',
  'ALREADY_POSTED',
  'AP_BILL_CURRENCY_REVIEW_REQUIRED',
  'AP_WITHHOLDING_SLOT_UNSUPPORTED',
  'AP_SETTLEMENT_COORDINATOR_UNAVAILABLE',
  'AP_SETTLEMENT_GROUP_ADJUSTMENTS_UNSUPPORTED',
  'BANK_TX_NOT_FOUND',
  'BILL_NOT_APPROVED',
  'PROVIDER_SETTLEMENT_AMOUNT_EXCEEDS_OPEN_BALANCE',
  'PROVIDER_SETTLEMENT_BILL_STATUS_INVALID',
  'PROVIDER_SETTLEMENT_EVIDENCE_CONFLICT',
  'PROVIDER_SETTLEMENT_EVIDENCE_INVALID',
  'PROVIDER_SETTLEMENT_ID_MISSING',
  'PROVIDER_SETTLEMENT_PAYMENT_BILL_CHANGED',
  'PROVIDER_SETTLEMENT_RAIL_UNKNOWN',
  'PROVIDER_SETTLEMENT_SCHEDULE_REQUIRED',
  'PROVIDER_SETTLEMENT_SOURCE_MISMATCH',
  'PROVIDER_SETTLEMENT_TERMINAL_CONFLICT',
  'TRANSFER_CHARGE_SIDE_REQUIRED',
  'TRANSFER_CHARGE_CLASSIFICATION_REQUIRED',
  'TRANSFER_CHARGE_CLASSIFICATION_INVALID',
  'TRANSFER_CHARGE_FX_EVIDENCE_REQUIRED',
  'TRANSFER_CHARGE_JURISDICTION_MISMATCH',
  'TRANSFER_CHARGE_AMOUNT_INVALID',
  'TRANSFER_CHARGE_AMOUNT_MISMATCH',
  'TRANSFER_CHARGE_TYPE_UNSUPPORTED',
  'TRANSFER_CHARGE_ATTRIBUTION_REQUIRED',
  'TRANSFER_CHARGE_PARTIAL_NOT_SUPPORTED',
  'TRANSFER_CHARGE_EXISTING_POSTING',
  'TRANSFER_CHARGE_POSTING_FAILED',
  // ALC-4255 — card provider fee posting is routed by the book's accounting framework
  // (ADR 0022). Both are fail-closed: before the router existed, an unknown fee slot silently
  // posted to the Brazilian bank-tariff account.
  'AP_BANK_FEE_FRAMEWORK_UNSUPPORTED',
  'AP_BANK_FEE_SLOT_UNSUPPORTED',
  // ALC-4254 — the framework owns the fee, but the entity's chart of accounts maps no account to
  // the rule's slot on the fee's date. Named rather than escaping as an untyped batch `error`.
  'AP_BANK_FEE_SLOT_NOT_MAPPED',
  'TAX_REGIME_NOT_CONFIGURED',
  'OP_IN_TRANSIT_MISSING',
  'OP_IN_TRANSIT_ROOT_NOT_FOUND',
  'OP_IN_TRANSIT_ROOT_WRONG_VERSION',
  // --- ALC-4241 additions ---
  // Reconciliation v2 adjustment posting. Registered here so the reconciliation transport maps
  // each refusal to a 400 with its stable code instead of letting it escape as a 500. The codes
  // themselves are declared once, in `decision-queue-v2.ts`, and spread in — listing them again
  // is what let `CSRF_AMOUNT_INVALID` fall through to a 500 in round 1.
  ...RECON_V2_ADJUSTMENT_ERROR_CODES,
  /**
   * A single AR link was retried inside a group that settles several receivables from one
   * movement **and carries adjustments attributed to specific ones**. Posting one link alone
   * would compare a part against the whole and strand every adjustment attributed elsewhere
   * (ALC-4241).
   *
   * A multi-link group with only the legacy pro-rated adjustments — or none — is not refused:
   * it retries link by link exactly as it did before this slice.
   */
  'AR_RECEIPT_RETRY_REQUIRES_WHOLE_GROUP',
  // --- ALC-4242 round 4, item 7 ---
  /**
   * The month's statement was closed while the journal was being posted (decision D-S6b).
   *
   * A GL posting helper opens its own transaction and never throws for an expected failure —
   * it returns one. Without a code of its own this refusal came back as `UNKNOWN` carrying the
   * guard's English developer message, bank account UUID and all: the caller could not tell a
   * rule it can act on from a database that fell over, and the UUID was rendered to whoever
   * was looking. The code is the same string the REST and MCP adapters use, so one refusal has
   * one name everywhere.
   */
  'RECON_V2_STATEMENT_CLOSED',
  'UNKNOWN',
  // --- ALC-4322 additions (internal operation S0) ---
  /** A bank leg's currency differs from a posting book's functional currency: nothing posts. */
  'CASH_MOVEMENT_REQUIRES_CONVERSION',
  /** A withholding leg reached a book that is not Brazilian BR_CPC: nothing posts. */
  'WITHHOLDING_JURISDICTION_UNSUPPORTED',
  /** A yield reversal (DEBIT) carries withholding metadata: nothing posts. */
  'INVESTMENT_YIELD_DEBIT_WITHHOLDING_UNSUPPORTED',
  /** SKIPPED: a self-link that does not await a pair takes no transit leg. */
  'SINGLE_LEG_TRANSIT_NOT_APPLICABLE_FOR_OPERATION',
  // --- ALC-4322 r2 additions (m6) ---
  /**
   * A yield or investment-tax line whose (or whose account's) currency is not established:
   * nothing posts, and v2 refuses the reconcile (decision I2).
   */
  'CASH_MOVEMENT_CURRENCY_UNTRUSTED',
  /** SKIPPED: a movement of 0,00 has nothing to book; v2 refuses to reconcile it. */
  'ZERO_OR_NEGATIVE_AMOUNT',
  /** SKIPPED (ALC-4324): the line is inside "Fim do histórico importado"; the Razão holds it. */
  'HISTORICAL_LEDGER_PERIOD',
  /** FAILED (ALC-4324): a transfer split across that date whose posting leg has charges or IRRF. */
  'HISTORICAL_LEDGER_SPLIT_TRANSFER_REQUIRES_ACCOUNTING',
  /**
   * FAILED (ALC-4324 K1b): a transfer split across that date whose inside leg's Razão entry does
   * not show the other bank or the transit account. Nothing posts.
   */
  'HISTORICAL_LEDGER_SPLIT_TRANSFER_TRANSIT_NOT_IN_RAZAO',
  /**
   * FAILED (ALC-4326 carry 3): the Razão booked the inside leg directly against the other bank,
   * and the outside leg had already posted its own transit leg while it waited for its pair.
   * Posting nothing more is the only way not to book the outside bank twice.
   */
  'HISTORICAL_LEDGER_SPLIT_TRANSFER_TRANSIT_ALREADY_POSTED',
  // --- ALC-4328 additions (internal operation S2b) ---
  /**
   * SKIPPED: the excess leg a split Resgate creates on its investment account (the income above
   * the carrying amount). It has no journal of its own; the income is on the Resgate's journal.
   */
  'RESGATE_EXCESS_LEG_NO_JOURNAL',
  /**
   * Refused (ALC-4328 round 1, O3): a GL retry, or the v1 re-submit, of a route (b) Resgate pair.
   * Only the split planner posts a Resgate; a retry would post a plain transfer. Nothing posts.
   */
  'INVESTMENT_RESGATE_RETRY_REFUSED',
  // --- ALC-4329 additions (internal operation S3) ---
  /**
   * FAILED / refused (S2b-AC10, S3-AC2): a Resgate paired across two São Paulo days. A Resgate
   * posts only same-day (its split reads the carrying amount on one day). Nothing posts.
   */
  'CROSS_DAY_INVESTMENT_REDEMPTION_REQUIRES_ACCOUNTING',
  /**
   * SKIPPED (S3-AC5): a same-account sweep pair the user confirmed (`meta.pairKind =
   * 'SAME_ACCOUNT_SWEEP'`). It nets to 0,00 on the account, so no journal.
   */
  'SAME_ACCOUNT_SWEEP_NET_ZERO',
  /**
   * FAILED (S3-AC6): a transfer link whose two legs are on the same bank account and that is not
   * a confirmed sweep pair. Nothing posts.
   */
  'TRANSFER_SAME_ACCOUNT',
  // --- ALC-4359 round 1 (backend MAJOR-1) ---
  /**
   * Refused: the movement's bank account was deleted before this post-commit GL posting took the
   * line lock. The same string the REST and MCP adapters use. Nothing posts.
   */
  'RECON_V2_BANK_ACCOUNT_DELETED',
  // --- ALC-4235 Charlie ACC-09 ---
  /**
   * Refused: a yield / investment-tax journal was posted but the movement has no self-link to
   * record it on (the reconciliation was undone in between). The posting transaction rolls back
   * with the journal, so the undo is never left with a POSTED journal it cannot find.
   */
  'MOVEMENT_POSTING_ROW_NO_SELF_LINK',
  // --- ALC-4235 ccy-s3-ar: every AR receipt refusal, from its own module (copy is total there) ---
  ...AR_RECEIPT_REFUSAL_CODES,
  // --- ADR 0031 payment mismatch: from its own module (copy is total there) ---
  ...PAYMENT_MISMATCH_REFUSAL_CODES,
] as const;

export type ReconciliationGlPostingErrorCode =
  (typeof RECONCILIATION_GL_POSTING_ERROR_CODES)[number];

/**
 * Localized, actionable transport messages for stable reconciliation posting refusals.
 *
 * The ALC-4241 `RECON_V2_ADJUSTMENT_*` codes are deliberately absent: their single pt-BR map is
 * `RECON_V2_ERROR_MESSAGES` in `packages/services/src/reconciliation/v2/errors.ts`, and a spec
 * there asserts every one of them has copy. Duplicating them here is what would let the two
 * drift.
 */
export const RECONCILIATION_GL_POSTING_ERROR_MESSAGES: Partial<
  Record<ReconciliationGlPostingErrorCode, { en: string; ptBr: string }>
> = {
  CASH_ACCOUNT_GL_LINK_REQUIRED: {
    en: 'This entity links bank accounts and cards straight to the chart, and this one has no GL account linked. Link an analytic account to it and try again.',
    ptBr: 'Esta entidade vincula contas bancárias e cartões direto ao plano de contas, e este ainda não tem conta contábil vinculada. Vincule uma conta analítica a ele e tente novamente.',
  },
  CASH_ACCOUNT_GL_LINK_INVALID: {
    en: 'The GL account linked to this bank account or card can no longer receive postings. Link an active analytic account of the right kind and try again.',
    ptBr: 'A conta contábil vinculada a esta conta bancária ou cartão não aceita mais lançamentos. Vincule uma conta analítica ativa do tipo certo e tente novamente.',
  },
  CASH_ACCOUNT_GL_LINK_CONFLICT: {
    en: 'The cards on this invoice are linked to different GL accounts. Link them all to the same liability account and try again.',
    ptBr: 'Os cartões desta fatura estão vinculados a contas contábeis diferentes. Vincule todos à mesma conta de passivo e tente novamente.',
  },
  RECON_V2_STATEMENT_CLOSED: {
    en: 'The statement for this month is already closed. Reopen it to make changes.',
    ptBr: 'O extrato deste mês já está fechado. Reabra para alterar.',
  },
  STATUTORY_EXPLICIT_ALLOCATION_INVALID_TARGET: {
    en: 'Select an approved tax guide for each allocation.',
    ptBr: 'Selecione uma guia aprovada para cada alocação.',
  },
  STATUTORY_EXPLICIT_ALLOCATION_INVALID: {
    en: 'Enter positive amounts at currency precision without duplicate components.',
    ptBr: 'Informe valores positivos na precisão da moeda, sem repetir componentes.',
  },
  STATUTORY_EXPLICIT_ALLOCATION_TOTAL_MISMATCH: {
    en: 'The component total must equal the reconciled payment amount.',
    ptBr: 'A soma dos componentes deve ser igual ao valor conciliado.',
  },
  STATUTORY_EXPLICIT_ALLOCATION_EXCEEDS_REMAINING: {
    en: 'The allocation exceeds the remaining approved component. Refresh and review it.',
    ptBr: 'A alocação excede o saldo aprovado do componente. Atualize e confira novamente.',
  },
  STATUTORY_PAYMENT_EVIDENCE_FORBIDDEN: {
    en: 'You do not have access to all accounting evidence for this payment.',
    ptBr: 'Você não tem acesso a toda a evidência contábil deste pagamento.',
  },
  STATUTORY_PAYMENT_BOOK_BALANCE_MISMATCH: {
    en: 'The books have different remaining guide balances. Review earlier payments.',
    ptBr: 'Os livros apresentam saldos diferentes para esta guia. Revise os pagamentos anteriores.',
  },

  AP_SETTLEMENT_ADJUSTMENT_SEMANTIC_REQUIRED: {
    en: 'This reconciliation adjustment has no verified economic meaning. Review and classify it before completing the AP settlement.',
    ptBr: 'Este ajuste de conciliação não tem natureza econômica confirmada. Revise e classifique o ajuste antes de concluir a liquidação da conta a pagar.',
  },
  AP_SETTLEMENT_OVERPAYMENT_EXCESS_UNSUPPORTED: {
    en: 'An excess payment cannot be settled as rounding. Review the excess separately before retrying; automatic conversion to a supplier advance is not supported.',
    ptBr: 'Um pagamento excedente não pode ser liquidado como arredondamento. Revise o excedente separadamente antes de tentar novamente; a conversão automática em adiantamento a fornecedor não está disponível.',
  },
  FOREIGN_SETTLEMENT_ACTIVE_PROVISION_REQUIRED: {
    en: 'The AP foreign settlement needs a posted provision with matching settlement evidence before it can be completed.',
    ptBr: 'A liquidação em moeda estrangeira precisa de uma provisão lançada com evidência de câmbio correspondente antes de ser concluída.',
  },
  FOREIGN_SETTLEMENT_ALLOCATION_REQUIRED: {
    en: 'Allocate the payment in the bill currency before confirming this reconciliation. The imported transaction remains pending.',
    ptBr: 'Informe a alocação do pagamento na moeda da conta a pagar antes de confirmar a conciliação. A transação importada continua pendente.',
  },
  SETTLEMENT_PROVISION_BOOK_SET_INCOMPLETE: {
    en: 'The AP settlement cannot be completed because its active provision journals do not match the expected book set.',
    ptBr: 'Não é possível concluir a liquidação: os lançamentos de provisão ativos não correspondem ao conjunto de livros esperado. Revise a provisão da conta a pagar em todos os livros antes de tentar novamente.',
  },
  AP_BANK_FEE_FRAMEWORK_UNSUPPORTED: {
    en: "This entity's accounting book has no automatic posting rule for card fees. Post the transaction manually.",
    ptBr:
      'O livro contábil desta entidade não tem regra de lançamento automático para tarifas de cartão. ' +
      'Lance a transação manualmente.',
  },
  AP_BANK_FEE_SLOT_UNSUPPORTED: {
    en: "This fee points at a system account this entity's accounting book does not recognize. Review the fee before posting it.",
    ptBr:
      'Esta tarifa aponta para uma conta de sistema que o livro contábil da entidade não reconhece. ' +
      'Revise a tarifa antes de lançá-la.',
  },
  AP_BANK_FEE_SLOT_NOT_MAPPED: {
    en: 'No chart-of-accounts account is mapped to this card fee on its date. Map the system account before posting it.',
    ptBr:
      'Nenhuma conta do plano de contas está mapeada para esta tarifa de cartão na data do lançamento. ' +
      'Mapeie a conta de sistema antes de lançá-la.',
  },
  AR_RECEIPT_RETRY_REQUIRES_WHOLE_GROUP: {
    en: 'This receipt settles more than one invoice and its adjustments belong to specific invoices, so it has to be reprocessed as a whole. Reprocess the reconciliation instead of a single invoice.',
    ptBr: 'Este recebimento baixa mais de um título e os ajustes pertencem a títulos específicos, então ele precisa ser reprocessado por inteiro. Reprocesse a conciliação em vez de um título isolado.',
  },
  // --- ALC-4322 additions (internal operation S0) ---
  CASH_MOVEMENT_REQUIRES_CONVERSION: {
    en: "The bank account's currency differs from the book's functional currency. Nothing was posted.",
    ptBr: 'A moeda da conta bancária é diferente da moeda funcional do livro. Nada foi contabilizado.',
  },
  WITHHOLDING_JURISDICTION_UNSUPPORTED: {
    en: 'Withholding tax is only posted for companies in Brazil in this version.',
    ptBr: 'Imposto retido na fonte só é contabilizado para empresas no Brasil nesta versão.',
  },
  INVESTMENT_YIELD_DEBIT_WITHHOLDING_UNSUPPORTED: {
    en: 'A yield reversal with withholding tax is not supported. Reconcile the tax separately.',
    ptBr: 'Estorno de rendimento com imposto retido não é suportado. Concilie o imposto separadamente.',
  },
  SINGLE_LEG_TRANSIT_NOT_APPLICABLE_FOR_OPERATION: {
    en: 'This movement does not await a pair; the transit account does not apply.',
    ptBr: 'Este movimento não aguarda par; a conta de trânsito não se aplica.',
  },
  // --- ALC-4322 r2 additions (m6) ---
  CASH_MOVEMENT_CURRENCY_UNTRUSTED: {
    en: "The account's currency is not confirmed. Confirm it in Settings → Accounts, then reconcile again. Nothing was posted.",
    ptBr: 'Moeda da conta não confirmada. Confirme a moeda em Configurações → Contas e concilie novamente. Nada foi contabilizado.',
  },
  ZERO_OR_NEGATIVE_AMOUNT: {
    en: 'The movement amount is zero, so there is nothing to post. Review the statement line.',
    ptBr: 'O valor do movimento é zero, então não há o que contabilizar. Revise a linha do extrato.',
  },
  HISTORICAL_LEDGER_PERIOD: {
    en: 'Already booked in the imported history — no journal.',
    ptBr: 'Já contabilizado no histórico importado — sem lançamento.',
  },
  HISTORICAL_LEDGER_SPLIT_TRANSFER_REQUIRES_ACCOUNTING: {
    en: 'One leg of this transfer is in the imported history and the other has fees or withholding. Book the other leg by hand.',
    ptBr: 'Uma perna desta transferência está no histórico importado e a outra tem tarifa ou retenção. Contabilize a outra perna manualmente.',
  },
  HISTORICAL_LEDGER_SPLIT_TRANSFER_TRANSIT_NOT_IN_RAZAO: {
    en: 'The imported history does not show how this transfer was booked. Check the imported history before posting.',
    ptBr: 'O histórico importado não mostra como esta transferência foi contabilizada. Verifique o histórico importado antes de lançar.',
  },
  HISTORICAL_LEDGER_SPLIT_TRANSFER_TRANSIT_ALREADY_POSTED: {
    en: 'The imported history already booked this transfer directly, and the other leg was already posted through the transit account. Undo this transfer reconciliation, which reverses the transit entry, then pair the two movements again.',
    ptBr: 'O histórico importado já contabilizou esta transferência direto entre os bancos, e a outra perna já foi lançada pela conta de trânsito. Desfaça esta conciliação de transferência, o que estorna o lançamento em trânsito, e pareie os dois movimentos de novo.',
  },
  // --- ALC-4328 additions (internal operation S2b) ---
  RESGATE_EXCESS_LEG_NO_JOURNAL: {
    en: 'The part of this redemption above the investment balance is financial income. It is posted in the redemption entry, not on this movement.',
    ptBr: 'A parte deste resgate acima do saldo contábil da aplicação é receita financeira. Ela está no lançamento do resgate, não neste movimento.',
  },
  INVESTMENT_RESGATE_RETRY_REFUSED: {
    en: 'This is an investment redemption, and it cannot be reposted by a retry. Undo the reconciliation and pick Resgate again. Nothing was posted.',
    ptBr: 'Este movimento é um resgate de aplicação e não pode ser relançado por nova tentativa. Desfaça a conciliação e escolha Resgate de novo. Nada foi contabilizado.',
  },
  // --- ALC-4329 additions (internal operation S3) ---
  CROSS_DAY_INVESTMENT_REDEMPTION_REQUIRES_ACCOUNTING: {
    en: 'A redemption is reconciled only with the investment movement of the same day. Wait for that movement, or pick the redemption against the investment account with no feed. Nothing was posted.',
    ptBr: 'Um resgate só é conciliado com o movimento da aplicação do mesmo dia. Aguarde esse movimento ou concilie o resgate contra a conta de investimento sem integração. Nada foi contabilizado.',
  },
  SAME_ACCOUNT_SWEEP_NET_ZERO: {
    en: 'Automatic sweep within the same account — no journal.',
    ptBr: 'Aplicação automática na mesma conta — sem lançamento.',
  },
  TRANSFER_SAME_ACCOUNT: {
    en: 'Both movements are on the same bank account, so this is not a transfer. Nothing was posted.',
    ptBr: 'Os dois movimentos estão na mesma conta bancária, então não é uma transferência. Nada foi contabilizado.',
  },
  // --- ALC-4359 round 1 (backend MAJOR-1) ---
  RECON_V2_BANK_ACCOUNT_DELETED: {
    en: 'This bank account was deleted before the entry could be posted. Nothing was posted.',
    ptBr: 'Esta conta bancária foi excluída antes de o lançamento ser feito. Nada foi contabilizado.',
  },
  // --- ALC-4235 Charlie ACC-09 ---
  MOVEMENT_POSTING_ROW_NO_SELF_LINK: {
    en: 'The reconciliation of this internal operation no longer exists, so the journal entry was not posted. Reload the queue and reconcile again.',
    ptBr: 'A conciliação desta operação interna não existe mais, então o lançamento contábil não foi gravado. Atualize a fila e concilie de novo.',
  },
  // --- ALC-4235 ccy-s3-ar ---
  ...AR_RECEIPT_REFUSAL_MESSAGES,
  // --- ADR 0031 payment mismatch ---
  ...PAYMENT_MISMATCH_REFUSAL_MESSAGES,
};

export function isReconciliationGlPostingErrorCode(
  value: unknown,
): value is ReconciliationGlPostingErrorCode {
  return (
    typeof value === 'string' &&
    (RECONCILIATION_GL_POSTING_ERROR_CODES as readonly string[]).includes(value)
  );
}
